import { Boot, DomEditor } from '@wangeditor/editor'
import { h } from 'snabbdom'
import { Editor as SlateEditor, Transforms } from 'slate'

// wangEditor/Slate 中用于识别发文正文图片节点的唯一类型。
const ARTICLE_IMAGE_TYPE = 'article-image'
// 防止热更新或组件重复挂载时多次向 wangEditor 注册同一模块。
let registered = false

/** 将任意宽度输入规范到 10%—100%。 */
function clampWidth(value) {
    const width = Number(value)
    if (!Number.isFinite(width)) return 100
    return Math.min(100, Math.max(10, Math.round(width * 100) / 100))
}

/** 将未知对齐值规范为 left、center 或 right。 */
function normalizeAlign(value) {
    return value === 'left' || value === 'right' ? value : 'center'
}

/** 转义序列化到 HTML 属性或图片说明中的文本。 */
function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

/**
 * 将文章图片 Slate 节点渲染为编辑器内的不可编辑图块。
 * 图块自身不可直接编辑，属性由上方排版工具栏修改。
 */
function renderArticleImage(elemNode, children, editor) {
    const { resourceId, src, alt, caption } = elemNode
    const width = clampWidth(elemNode.width)
    const align = normalizeAlign(elemNode.align)
    const selected = DomEditor.isNodeSelected(editor, elemNode)

    const marginStyle = align === 'left'
        ? { marginLeft: '0', marginRight: 'auto' }
        : align === 'right'
            ? { marginLeft: 'auto', marginRight: '0' }
            : { marginLeft: 'auto', marginRight: 'auto' }

    // 记录拖拽起点，通过容器宽度把水平位移换算成相对百分比。
    const beginResize = event => {
        event.preventDefault()
        event.stopPropagation()
        const root = event.currentTarget.parentElement
        const container = root && root.parentElement
        if (!root || !container) return

        const startX = event.clientX
        const startWidth = width
        const containerWidth = container.getBoundingClientRect().width || 1
        let frame = 0
        let nextWidth = startWidth
        let path
        try {
            path = DomEditor.findPath(editor, elemNode)
        } catch (e) {
            return
        }

        // 使用 requestAnimationFrame 合并高频 mousemove，减少编辑器节点更新次数。
        const applyWidth = () => {
            frame = 0
            try {
                Transforms.setNodes(editor, { width: clampWidth(nextWidth) }, { at: path })
                editor.emit('article-image-resize')
            } catch (e) {
                // 节点已被撤销或删除时终止本次拖拽。
            }
        }
        const move = moveEvent => {
            nextWidth = startWidth + ((moveEvent.clientX - startX) / containerWidth) * 100
            if (!frame) frame = window.requestAnimationFrame(applyWidth)
        }
        const stop = () => {
            if (frame) window.cancelAnimationFrame(frame)
            applyWidth()
            document.removeEventListener('mousemove', move)
            document.removeEventListener('mouseup', stop)
        }
        document.addEventListener('mousemove', move)
        document.addEventListener('mouseup', stop)
    }

    const childNodes = [
        h('img.article-image-preview', {
            props: { src: src || '', alt: alt || '', draggable: false }
        })
    ]
    if (caption) childNodes.push(h('div.article-image-caption', caption))
    childNodes.push(h('span.article-image-resize-handle', {
        props: { title: '拖拽调整图片宽度' },
        on: { mousedown: beginResize }
    }))

    return h('div.article-image-block', {
        props: { contentEditable: false },
        dataset: {
            articleImage: resourceId || '',
            align,
            selected: selected ? 'true' : 'false'
        },
        style: {
            width: `${width}%`,
            maxWidth: '100%',
            position: 'relative',
            ...marginStyle
        },
        on: {
            click: event => {
                event.preventDefault()
                editor.emit('article-image-select', elemNode)
            }
        }
    }, childNodes)
}

/** 将文章图片节点序列化为可入库的语义 figure HTML。 */
function articleImageToHtml(elemNode) {
    const resourceId = escapeHtml(elemNode.resourceId)
    const src = escapeHtml(elemNode.src)
    const alt = escapeHtml(elemNode.alt)
    const caption = escapeHtml(elemNode.caption)
    const width = clampWidth(elemNode.width)
    const align = normalizeAlign(elemNode.align)
    const captionHtml = caption ? `<figcaption>${caption}</figcaption>` : ''
    return `<figure data-article-image="${resourceId}" data-align="${align}" style="width:${width}%"><img src="${src}" alt="${alt}">${captionHtml}</figure>`
}

/** 将服务端返回的 figure HTML 解析为文章图片 Slate 节点。 */
function parseArticleImageHtml(elem) {
    const image = elem.querySelector('img')
    const caption = elem.querySelector('figcaption')
    const styleWidth = (elem.getAttribute('style') || '').match(/(?:^|;)\s*width\s*:\s*(\d+(?:\.\d+)?)%/i)
    return {
        type: ARTICLE_IMAGE_TYPE,
        resourceId: elem.getAttribute('data-article-image') || '',
        src: image ? image.getAttribute('src') || '' : '',
        alt: image ? image.getAttribute('alt') || '' : '',
        caption: caption ? caption.textContent || '' : '',
        width: clampWidth(styleWidth ? styleWidth[1] : 100),
        align: normalizeAlign(elem.getAttribute('data-align')),
        children: [{ text: '' }]
    }
}

// 自定义模块同时定义渲染、序列化、解析以及 void 节点行为。
const articleImageModule = {
    renderElems: [{ type: ARTICLE_IMAGE_TYPE, renderElem: renderArticleImage }],
    elemsToHtml: [{ type: ARTICLE_IMAGE_TYPE, elemToHtml: articleImageToHtml }],
    parseElemsHtml: [{ selector: 'figure[data-article-image]', parseElemHtml: parseArticleImageHtml }],
    editorPlugin(editor) {
        const { isVoid, normalizeNode } = editor
        editor.isVoid = elem => elem.type === ARTICLE_IMAGE_TYPE || isVoid(elem)
        editor.normalizeNode = entry => {
            const [node, path] = entry
            if (node.type === ARTICLE_IMAGE_TYPE) {
                // void 图片不能成为编辑器最后一个节点，保留空段落便于继续输入正文。
                if (DomEditor.isLastNode(editor, node)) {
                    Transforms.insertNodes(editor, DomEditor.genEmptyParagraph(), { at: [path[0] + 1] })
                }
                return
            }
            normalizeNode(entry)
        }
        return editor
    }
}

/** 注册发文图片模块；同一页面生命周期内只执行一次。 */
export function registerArticleImageModule() {
    if (registered) return
    Boot.registerModule(articleImageModule)
    registered = true
}

/** 在编辑器当前选区插入一个新的文章图片图块。 */
export function insertArticleImage(editor, resource) {
    if (!editor || !resource) return
    const node = {
        type: ARTICLE_IMAGE_TYPE,
        resourceId: resource.id,
        src: resource.url || resource.path,
        alt: resource.name || '图片',
        caption: '',
        width: 100,
        align: 'center',
        children: [{ text: '' }]
    }
    editor.restoreSelection()
    Transforms.insertNodes(editor, node)
    editor.focus()
}

/** 获取当前选中的文章图片节点。 */
export function getSelectedArticleImage(editor) {
    if (!editor) return null
    return DomEditor.getSelectedNodeByType(editor, ARTICLE_IMAGE_TYPE)
}

/** 将编辑器选区定位到指定文章图片节点。 */
export function selectArticleImage(editor, node) {
    if (!editor || !node) return false
    try {
        const path = DomEditor.findPath(editor, node)
        Transforms.select(editor, path)
        return true
    } catch (e) {
        return false
    }
}

/** 更新当前图片图块的宽度、对齐、替代文本、说明或资源信息。 */
export function updateSelectedArticleImage(editor, patch) {
    const node = getSelectedArticleImage(editor)
    if (!node) return false
    try {
        const path = DomEditor.findPath(editor, node)
        Transforms.setNodes(editor, patch, { at: path })
        editor.emit('article-image-updated')
        return true
    } catch (e) {
        return false
    }
}

/** 仅从正文移除当前图片节点，资源记录仍保留在资源面板。 */
export function removeSelectedArticleImage(editor) {
    const node = getSelectedArticleImage(editor)
    if (!node) return false
    try {
        const path = DomEditor.findPath(editor, node)
        Transforms.removeNodes(editor, { at: path })
        editor.emit('article-image-updated')
        return true
    } catch (e) {
        return false
    }
}

/** 统计当前未保存编辑内容中每个资源 ID 的引用次数。 */
export function getArticleImageReferenceCounts(editor) {
    const counts = {}
    if (!editor) return counts
    for (const [node] of SlateEditor.nodes(editor, {
        at: [],
        match: node => node && node.type === ARTICLE_IMAGE_TYPE
    })) {
        if (node.resourceId) counts[node.resourceId] = (counts[node.resourceId] || 0) + 1
    }
    return counts
}

/** 定位并滚动到正文中首次引用指定资源的图片图块。 */
export function focusArticleImage(editor, resourceId) {
    if (!editor || !resourceId) return false
    for (const [node, path] of SlateEditor.nodes(editor, {
        at: [],
        match: node => node && node.type === ARTICLE_IMAGE_TYPE && node.resourceId === resourceId
    })) {
        Transforms.select(editor, path)
        try {
            const dom = DomEditor.toDOMNode(editor, node)
            dom.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } catch (e) { }
        return true
    }
    return false
}

// 模块必须在 Editor 创建前完成注册。
registerArticleImageModule()
